import React, { useState, useEffect, useCallback } from 'react';
import DashboardLayout from '../../components/layout/DashboardLayout';
import { reportsAPI, adminOrgAPI } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import { useOrg } from '../../context/OrgContext';
import {
  HiOutlineDocumentReport, HiOutlineDownload, HiOutlineRefresh,
  HiOutlineSearch, HiOutlineCalendar, HiOutlineFilter, HiOutlineChevronLeft,
  HiOutlineChevronRight, HiOutlineOfficeBuilding, HiOutlineUserGroup
} from 'react-icons/hi';
import toast from 'react-hot-toast';
import Pagination from '../../components/common/Pagination';

const ROLE_OPTIONS = [
  { value: 'all', label: 'All Roles (Full Comprehensive Report)' },
  { value: 'gate_guard', label: 'Gate Operations (Gate Guard)' },
  { value: 'supervisor', label: 'Production Management (Supervisor)' },
  { value: 'quality_checker', label: 'Quality Control (QC Checker)' },
  { value: 'store_manager', label: 'Store & Godown (Store Manager)' },
  { value: 'sales', label: 'Sales & Orders (Sales Officer)' }
];

const getTodayString = () => new Date().toISOString().split('T')[0];

const getFirstDayOfMonthString = () => {
  const d = new Date();
  d.setDate(1);
  return d.toISOString().split('T')[0];
};

const ReportsDashboard = () => {
  const { user } = useAuth();
  const { currencySymbol, formatCurrency } = useOrg();

  // Filters state
  const [dateMode, setDateMode] = useState('single'); // 'single' or 'range'
  const [singleDate, setSingleDate] = useState(getTodayString());
  const [startDate, setStartDate] = useState(getFirstDayOfMonthString());
  const [endDate, setEndDate] = useState(getTodayString());
  const [targetRole, setTargetRole] = useState('all');
  const [targetOrgId, setTargetOrgId] = useState('all');
  const [search, setSearch] = useState('');

  // Data & Pagination state
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);

  // Organizations list for SuperAdmin
  const [organizations, setOrganizations] = useState([]);

  const isSuperAdmin = user?.role === 'super_admin';
  const isAdmin = ['super_admin', 'admin'].includes(user?.role);

  // Load organizations for SuperAdmin dropdown
  useEffect(() => {
    if (isSuperAdmin) {
      adminOrgAPI.getAll()
        .then(res => {
          if (res.data && res.data.success) {
            setOrganizations(res.data.data || []);
          }
        })
        .catch(() => {});
    }
  }, [isSuperAdmin]);

  const fetchReports = useCallback(async (targetPage = 1) => {
    setLoading(true);
    try {
      const params = {
        dateMode,
        singleDate: dateMode === 'single' ? singleDate : '',
        startDate: dateMode === 'range' ? startDate : '',
        endDate: dateMode === 'range' ? endDate : '',
        targetRole: isAdmin ? targetRole : user?.role,
        targetOrgId: isSuperAdmin ? targetOrgId : '',
        page: targetPage,
        limit: 20,
        search
      };

      const res = await reportsAPI.getData(params);
      if (res.data && res.data.success) {
        setReports(res.data.data || []);
        setTotalPages(res.data.totalPages || 1);
        setTotalCount(res.data.totalCount || 0);
        setPage(res.data.currentPage || 1);
      }
    } catch (err) {
      toast.error('Failed to load report logs');
    } finally {
      setLoading(false);
    }
  }, [dateMode, singleDate, startDate, endDate, targetRole, targetOrgId, search, isAdmin, isSuperAdmin, user?.role]);

  useEffect(() => {
    fetchReports(1);
  }, [fetchReports]);

  const [exporting, setExporting] = useState(false);

  const handleExportXLSX = async () => {
    const params = {
      dateMode,
      singleDate: dateMode === 'single' ? singleDate : '',
      startDate: dateMode === 'range' ? startDate : '',
      endDate: dateMode === 'range' ? endDate : '',
      targetRole: isAdmin ? targetRole : user?.role,
      targetOrgId: isSuperAdmin ? targetOrgId : ''
    };

    setExporting(true);
    const toastId = toast.loading('Generating Professional Excel Report (.xlsx)...');
    try {
      const response = await reportsAPI.exportXLSX(params);
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      const filename = `ERP_Report_${(isAdmin ? targetRole : (user?.role || 'ALL')).toUpperCase()}_${new Date().toISOString().split('T')[0]}.xlsx`;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Excel Report downloaded successfully!', { id: toastId });
    } catch (err) {
      console.error('Export error:', err);
      toast.error('Failed to export report. Please try again.', { id: toastId });
    } finally {
      setExporting(false);
    }
  };

  const getModuleBadgeColor = (module) => {
    switch (module) {
      case 'Gate Operations': return { bg: 'rgba(59, 130, 246, 0.12)', color: '#3b82f6', border: 'rgba(59, 130, 246, 0.3)' };
      case 'Production Management': return { bg: 'rgba(249, 115, 22, 0.12)', color: '#f97316', border: 'rgba(249, 115, 22, 0.3)' };
      case 'Quality Control': return { bg: 'rgba(168, 85, 247, 0.12)', color: '#a855f7', border: 'rgba(168, 85, 247, 0.3)' };
      case 'Store & Inventory': return { bg: 'rgba(16, 185, 129, 0.12)', color: '#10b981', border: 'rgba(16, 185, 129, 0.3)' };
      case 'Sales & Orders': return { bg: 'rgba(236, 72, 153, 0.12)', color: '#ec4899', border: 'rgba(236, 72, 153, 0.3)' };
      default: return { bg: 'rgba(255, 255, 255, 0.08)', color: 'var(--text-secondary)', border: 'var(--border)' };
    }
  };

  return (
    <DashboardLayout pageTitle="Analytics & Reports">
      <div style={{ maxWidth: '1280px', margin: '0 auto', paddingBottom: '40px' }}>

        {/* HEADER BAR */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px', flexWrap: 'wrap', gap: '16px' }}>
          <div>
            <h2 style={{ color: 'var(--text-primary)', fontSize: '1.4rem', fontWeight: 700, margin: 0, display: 'flex', alignItems: 'center', gap: '10px' }}>
              <HiOutlineDocumentReport style={{ color: 'var(--primary)' }} /> Factory Operational Reports
            </h2>
            <p style={{ color: 'var(--text-secondary)', fontSize: '0.88rem', margin: '4px 0 0 0' }}>
              Generate, filter, and export formal Excel (.xlsx) reports across all departments.
            </p>
          </div>

          <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
            <button className="btn btn-secondary btn-sm" onClick={() => fetchReports(page)} disabled={loading}>
              <HiOutlineRefresh /> Refresh
            </button>
            <button className="btn btn-primary" onClick={handleExportXLSX} disabled={exporting} style={{ display: 'flex', alignItems: 'center', gap: '8px', fontWeight: 600 }}>
              <HiOutlineDownload style={{ fontSize: '1.2rem' }} /> {exporting ? 'Generating Excel...' : 'Export to Excel (.xlsx)'}
            </button>
          </div>
        </div>

        {/* FILTER CONTROL CARD */}
        <div className="glass-card" style={{ padding: '24px', marginBottom: '24px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '16px', color: 'var(--text-primary)', fontWeight: 600, fontSize: '0.95rem' }}>
            <HiOutlineFilter style={{ color: 'var(--primary)' }} /> Report Filter Criteria
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '16px', alignItems: 'end' }}>

            {/* DATE MODE TOGGLE */}
            <div className="form-group" style={{ margin: 0 }}>
              <label className="form-label">Date Filter Mode</label>
              <div style={{ display: 'flex', background: 'var(--bg-secondary)', borderRadius: '8px', padding: '4px', border: '1px solid var(--border)' }}>
                <button
                  type="button"
                  onClick={() => setDateMode('single')}
                  style={{
                    flex: 1, padding: '8px', borderRadius: '6px', border: 'none',
                    background: dateMode === 'single' ? 'var(--primary)' : 'transparent',
                    color: dateMode === 'single' ? '#ffffff' : 'var(--text-secondary)',
                    fontWeight: 600, fontSize: '0.85rem', cursor: 'pointer', transition: 'var(--transition-fast)'
                  }}
                >
                  Single Date
                </button>
                <button
                  type="button"
                  onClick={() => setDateMode('range')}
                  style={{
                    flex: 1, padding: '8px', borderRadius: '6px', border: 'none',
                    background: dateMode === 'range' ? 'var(--primary)' : 'transparent',
                    color: dateMode === 'range' ? '#ffffff' : 'var(--text-secondary)',
                    fontWeight: 600, fontSize: '0.85rem', cursor: 'pointer', transition: 'var(--transition-fast)'
                  }}
                >
                  Date Range
                </button>
              </div>
            </div>

            {/* DATE INPUTS */}
            {dateMode === 'single' ? (
              <div className="form-group" style={{ margin: 0 }}>
                <label className="form-label">Select Date</label>
                <div className="form-input-icon">
                  <HiOutlineCalendar className="icon" />
                  <input
                    type="date"
                    className="form-input"
                    value={singleDate}
                    onChange={e => setSingleDate(e.target.value)}
                  />
                </div>
              </div>
            ) : (
              <>
                <div className="form-group" style={{ margin: 0 }}>
                  <label className="form-label">From Date</label>
                  <div className="form-input-icon">
                    <HiOutlineCalendar className="icon" />
                    <input
                      type="date"
                      className="form-input"
                      value={startDate}
                      onChange={e => setStartDate(e.target.value)}
                    />
                  </div>
                </div>
                <div className="form-group" style={{ margin: 0 }}>
                  <label className="form-label">To Date</label>
                  <div className="form-input-icon">
                    <HiOutlineCalendar className="icon" />
                    <input
                      type="date"
                      className="form-input"
                      value={endDate}
                      onChange={e => setEndDate(e.target.value)}
                    />
                  </div>
                </div>
              </>
            )}

            {/* ADMIN ROLE FILTER DROPDOWN */}
            {isAdmin && (
              <div className="form-group" style={{ margin: 0 }}>
                <label className="form-label">Filter Department / Role</label>
                <div className="form-input-icon">
                  <HiOutlineUserGroup className="icon" />
                  <select
                    className="form-input"
                    value={targetRole}
                    onChange={e => setTargetRole(e.target.value)}
                    style={{ paddingLeft: '45px', cursor: 'pointer' }}
                  >
                    {ROLE_OPTIONS.map(r => (
                      <option key={r.value} value={r.value}>{r.label}</option>
                    ))}
                  </select>
                </div>
              </div>
            )}

            {/* SUPERADMIN TENANT / ORG DROPDOWN */}
            {isSuperAdmin && (
              <div className="form-group" style={{ margin: 0 }}>
                <label className="form-label">Target Organization Tenant</label>
                <div className="form-input-icon">
                  <HiOutlineOfficeBuilding className="icon" />
                  <select
                    className="form-input"
                    value={targetOrgId}
                    onChange={e => setTargetOrgId(e.target.value)}
                    style={{ paddingLeft: '45px', cursor: 'pointer' }}
                  >
                    <option value="all">All Organizations (Global Master)</option>
                    {organizations.map(org => (
                      <option key={org._id} value={org._id}>{org.name}</option>
                    ))}
                  </select>
                </div>
              </div>
            )}

            {/* SEARCH INPUT */}
            <div className="form-group" style={{ margin: 0 }}>
              <label className="form-label">Search Keywords</label>
              <div className="form-input-icon">
                <HiOutlineSearch className="icon" />
                <input
                  type="text"
                  className="form-input"
                  placeholder="Search ref no, vehicle, model, item..."
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                />
              </div>
            </div>

          </div>
        </div>

        {/* DATA GRID TABLE CARD */}
        <div className="glass-card" style={{ padding: '0', overflow: 'hidden' }}>
          <div style={{ padding: '20px 24px', borderBottom: '1px solid var(--border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div style={{ fontWeight: 600, fontSize: '0.95rem', color: 'var(--text-primary)' }}>
              Filtered Report Logs ({totalCount} total entries found)
            </div>
            <div style={{ fontSize: '0.85rem', color: 'var(--text-muted)' }}>
              Page {page} of {totalPages}
            </div>
          </div>

          {loading ? (
            <div style={{ textAlign: 'center', padding: '60px', color: 'var(--text-dim)' }}>
              <div className="spinner" style={{ margin: '0 auto 12px' }} /> Fetching fast report database...
            </div>
          ) : reports.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '60px', color: 'var(--text-muted)' }}>
              <HiOutlineDocumentReport style={{ fontSize: '3rem', color: 'var(--text-dim)', marginBottom: '12px' }} />
              <h3>No Report Logs Found</h3>
              <p style={{ fontSize: '0.9rem' }}>Try adjusting your date range, role filter, or search terms.</p>
            </div>
          ) : (
            <div className="table-responsive" style={{ overflowX: 'auto', width: '100%' }}>
              <table className="table" style={{ width: '100%', minWidth: '950px', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ background: 'var(--bg-secondary)', textTransform: 'uppercase', fontSize: '0.75rem', letterSpacing: '0.5px' }}>
                    <th style={{ padding: '14px 20px', textAlign: 'left', whiteSpace: 'nowrap' }}>Department / Module</th>
                    <th style={{ padding: '14px 20px', textAlign: 'left', whiteSpace: 'nowrap' }}>Ref / Pass No</th>
                    <th style={{ padding: '14px 20px', textAlign: 'left', whiteSpace: 'nowrap' }}>Details & Title</th>
                    <th style={{ padding: '14px 20px', textAlign: 'left', whiteSpace: 'nowrap' }}>Metrics / Operator</th>
                    <th style={{ padding: '14px 20px', textAlign: 'left', whiteSpace: 'nowrap' }}>Status</th>
                    <th style={{ padding: '14px 20px', textAlign: 'right', whiteSpace: 'nowrap' }}>Date & Time</th>
                  </tr>
                </thead>
                <tbody>
                  {reports.map((item, idx) => {
                    const badge = getModuleBadgeColor(item.module);
                    return (
                      <tr key={item.id || idx} style={{ borderBottom: '1px solid var(--border)', transition: 'var(--transition-fast)' }}>
                        <td style={{ padding: '14px 20px', whiteSpace: 'nowrap' }}>
                          <span style={{
                            padding: '4px 12px', borderRadius: '20px', fontSize: '0.78rem', fontWeight: 600,
                            background: badge.bg, color: badge.color, border: `1px solid ${badge.border}`,
                            whiteSpace: 'nowrap', display: 'inline-block'
                          }}>
                            {item.module}
                          </span>
                        </td>
                        <td style={{ padding: '14px 20px', fontWeight: 700, fontFamily: 'monospace', fontSize: '0.9rem', color: 'var(--primary)', whiteSpace: 'nowrap' }}>
                          {item.refNo}
                        </td>
                        <td style={{ padding: '14px 20px', whiteSpace: 'nowrap' }}>
                          <div style={{ fontWeight: 600, color: 'var(--text-primary)', fontSize: '0.9rem' }}>{item.title}</div>
                          <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>{item.subtitle}</div>
                        </td>
                        <td style={{ padding: '14px 20px', fontSize: '0.85rem', color: 'var(--text-secondary)', whiteSpace: 'nowrap' }}>
                          {item.detail}
                        </td>
                        <td style={{ padding: '14px 20px', whiteSpace: 'nowrap' }}>
                          <span style={{
                            padding: '3px 8px', borderRadius: '4px', fontSize: '0.75rem', fontWeight: 600,
                            background: item.status?.toLowerCase().includes('reject') || item.status?.toLowerCase().includes('out') ? 'rgba(239, 68, 68, 0.12)' : 'rgba(34, 197, 94, 0.12)',
                            color: item.status?.toLowerCase().includes('reject') || item.status?.toLowerCase().includes('out') ? '#ef4444' : '#22c55e',
                            border: `1px solid ${item.status?.toLowerCase().includes('reject') || item.status?.toLowerCase().includes('out') ? 'rgba(239, 68, 68, 0.3)' : 'rgba(34, 197, 94, 0.3)'}`,
                            whiteSpace: 'nowrap', display: 'inline-block'
                          }}>
                            {item.status}
                          </span>
                        </td>
                        <td style={{ padding: '14px 20px', textAlign: 'right', fontSize: '0.82rem', color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>
                          {new Date(item.date).toLocaleString('en-IN', {
                            day: '2-digit', month: 'short', year: 'numeric',
                            hour: '2-digit', minute: '2-digit'
                          })}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {/* ALWAYS VISIBLE PAGINATION FOOTER */}
          <Pagination
            currentPage={page}
            totalPages={totalPages}
            totalItems={totalCount}
            itemsPerPage={20}
            onPageChange={fetchReports}
          />
        </div>

      </div>
    </DashboardLayout>
  );
};

export default ReportsDashboard;
